import { useSendNotification } from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import { emptyArray, useFetcher, useSWRWithDefaults } from "@app/lib/swr/swr";
import type {
  AgentEditorsLightResponseBody,
  AgentEditorsResponseBody,
  PatchAgentEditorsRequestBody,
} from "@app/types/api/assistant/configuration/editors";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { Fetcher } from "swr";

export function useEditors({
  owner,
  agentConfigurationId,
  disabled,
}: {
  owner: LightWorkspaceType;
  agentConfigurationId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const editorsFetcher: Fetcher<
    AgentEditorsResponseBody | AgentEditorsLightResponseBody
  > = fetcher;

  const { data, error, isValidating, mutate } = useSWRWithDefaults(
    agentConfigurationId
      ? `/api/w/${owner.sId}/assistant/agent_configurations/${agentConfigurationId}/editors`
      : null,

    editorsFetcher,
    {
      disabled,
    }
  );

  return {
    editors: data?.editors ?? emptyArray(),
    isEditorsLoading: !error && !data && !disabled,
    isEditorsError: !!error,
    isEditorsValidating: isValidating,
    mutateEditors: mutate,
  };
}

export function useUpdateEditors({
  owner,
  agentConfigurationId,
}: {
  owner: LightWorkspaceType;
  agentConfigurationId: string | null;
}) {
  const { t } = useTranslation();
  const sendNotification = useSendNotification();
  const { mutateEditors } = useEditors({
    owner,
    agentConfigurationId,
    disabled: true,
  });

  const updateAgentEditors = useCallback(
    async (body: PatchAgentEditorsRequestBody) => {
      if (!agentConfigurationId) {
        return false;
      }

      const res = await clientFetch(
        `/api/w/${owner.sId}/assistant/agent_configurations/${agentConfigurationId}/editors`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      if (res.ok) {
        await mutateEditors();

        let title = "";
        let description: string | undefined = undefined;
        if (
          body.addEditorIds != null &&
          body.addEditorIds.length > 0 &&
          body.removeEditorIds != null &&
          body.removeEditorIds.length > 0
        ) {
          title = t("toasts.agents.editorsUpdatedTitle");
          description = t("toasts.agents.editorsUpdatedDesc");
        } else if (
          (body.addEditorIds == null || body.addEditorIds.length <= 0) &&
          body.removeEditorIds != null &&
          body.removeEditorIds.length > 0
        ) {
          title = t("toasts.agents.editorsRemovedTitle", {
            count: body.removeEditorIds.length,
          });
        } else {
          title = t("toasts.agents.editorsAddedTitle", {
            count: body.addEditorIds?.length ?? 0,
          });
        }

        sendNotification({
          type: "success",
          title,
          description,
        });
        return true;
      }

      sendNotification({
        type: "error",
        title: t("toasts.agents.editorsUpdateFailedTitle"),
      });
      return false;
    },
    [owner, agentConfigurationId, mutateEditors, sendNotification, t]
  );

  return updateAgentEditors;
}
